import type { QueryClient } from '@tanstack/react-query';

/**
 * localStorage key used by the async-storage persister configured in `router.tsx`.
 * Kept in sync with the explicit `key` passed to `createAsyncStoragePersister`.
 */
export const PERSISTED_QUERY_CACHE_KEY = 'REACT_QUERY_OFFLINE_CACHE';

/**
 * Drop all cached query data from BOTH the in-memory client and the persisted
 * localStorage blob.
 *
 * `queryClient.clear()` only updates memory; the persister writes to localStorage
 * on a ~1s throttle, so we remove the key directly to guarantee a stale copy isn't
 * restored on the next load. Call this on auth transitions (login / logout / NGO
 * switch) so a session never inherits the previous state's cache — otherwise a
 * persisted `ngo.*` query result cached under the previous session could be restored
 * and read synchronously by the next NGO/user, showing stale (or someone else's) data
 * before the cookie-backed refetch can land.
 */
export function clearPersistedQueryCache(queryClient: QueryClient): void {
	queryClient.clear();
	if (typeof window !== 'undefined') {
		window.localStorage.removeItem(PERSISTED_QUERY_CACHE_KEY);
	}
}

/**
 * Run `restore` once the document has finished parsing. **The fallback trigger, not the main one** — see
 * the correction below.
 *
 * WHAT THIS WAS FOR (TEC-3758). The persisted cache and the SSR payload are two sources for the same
 * queries, and react-query resolves that by freshness: `hydrate` overwrites an entry only when the
 * incoming data is NEWER. What that cannot protect is the FIRST client render. Restoring while the router
 * was built put the previous visit's figures in the cache before that render, so `useSuspenseQuery`
 * resolved from them instead of suspending on the streamed promise, the render disagreed with the text
 * the server had already written, and React reported `#418 args[]=text` and regenerated the campaign card
 * subtree. The payload then won, correctly, but a frame too late.
 *
 * WHY THAT WAS NOT THE WHOLE FIX (TEC-3881). This comment used to claim that after deferring, "every query
 * the document carried already holds something newer, so the restore is a no-op for them". Both halves are
 * false, and the errors kept arriving for four more weeks:
 *
 * - A query the loader does not await is dehydrated PENDING, at `dataUpdatedAt: 0`. Nothing is newer than
 *   a previous visit there — any persisted copy beats it, in either order, so deferring alone changes
 *   nothing for exactly the queries the portal home reports on.
 * - `DOMContentLoaded` is not late enough on a document the browser RESTORED. Parsing finished before
 *   `getRouter()` ran, so this fires SYNCHRONOUSLY, ahead of the router's hydrate hook — with an empty
 *   cache, which is also what made the document-first filter a no-op until the restore moved.
 *
 * The restore now runs from inside the router's `hydrate` hook (see `router.tsx`), which is awaited before
 * the first render. This function stays for the one case that hook cannot cover: a document with no SSR
 * payload at all, where there is no document copy to lose to.
 *
 * `DOMContentLoaded` is still the signal here because a payload, when there is one, travels in inline
 * `<script>` tags at the end of `<body>`, and inline scripts block the parser.
 *
 * Restoring is deliberately NOT split from the save subscription. `persistQueryClient` restores and only
 * then subscribes, and that order matters: a save triggered before the restore would dehydrate the current
 * document over the blob and destroy the very entries we were about to read.
 */
export function afterSsrPayloadApplied(restore: () => void): void {
	if (typeof document === 'undefined') return;

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', () => restore(), { once: true });
		return;
	}

	restore();
}

/**
 * The live-counter queries, which must never reach `localStorage`.
 *
 * Their payload is a replay of one five-minute window: valuable for those five minutes and worthless
 * afterwards, since the counter asks for the current window on every load anyway. Persisting them is
 * what produced TEC-3807/TEC-3810 — a returning visitor restored a hundred-odd window entries, every
 * one of them stale, and TanStack Query refetched them together into a single 16 KB tRPC GET that the
 * service answered with a 500.
 *
 * The window key no longer travels in the query key (see `lib/live-window.ts`), so a fresh cache can no
 * longer grow that backlog. This predicate stays for two reasons: a blob already on a visitor's disk
 * still holds the old entries, and a persisted replay would be restored MINUTES out of date and shown
 * before the refetch lands.
 *
 * Matched by procedure path rather than by a prefix over `portal.*`, so no ordinary portal query loses
 * its warm start. `demo-recent-donations` is the internal counter's client-side fake: it never reaches
 * the network, but it accumulates in the same blob.
 */
export function isLiveDonationsQueryKey(queryKey: readonly unknown[]): boolean {
	const first = queryKey[0];
	if (first === 'demo-recent-donations') return true;
	if (!Array.isArray(first)) return false;
	return first[0] === 'portal' && (first[1] === 'getRecentDonations' || first[1] === 'getEmergencyDonations');
}

/**
 * Which queries are written to `localStorage`. Extracted from `router.tsx` so it can be unit-tested:
 * inline in the persister options it was unreachable, and the rule it encodes is exactly the kind that
 * breaks silently — a query that should not be persisted simply is, and nothing fails until a visitor
 * comes back days later.
 *
 * Cookies are the source of truth for auth. Persisting auth queries would make the previous user's
 * identity survive a page reload until the background refetch lands.
 */
export function shouldDehydrateQuery(query: {
	queryKey: readonly unknown[];
	state: { status: string; data?: unknown };
}): boolean {
	const first = query.queryKey[0];
	if (Array.isArray(first) && first[0] === 'auth') return false;
	if (isLiveDonationsQueryKey(query.queryKey)) return false;
	if (query.state.status !== 'success') return false;
	return true;
}

/** The shape `persistQueryClient` hands to `hydrate`: enough of it to filter, and nothing more. */
interface PersistedQuery {
	queryHash: string;
}

/**
 * The persisted entries that must NOT be restored, because this document already carries the query.
 *
 * WHY THE DEFERRAL ABOVE IS NOT ENOUGH (TEC-3881). `afterSsrPayloadApplied` assumes that once the
 * payload is applied "every query the document carried already holds something newer, so the restore is
 * a no-op for them". That holds only for a query the server had RESOLVED when it dehydrated. The portal
 * home's aggregates and the landing's recent campaigns are still PENDING at that moment — their loaders
 * deliberately do not await them, to protect shell TTFB — so they are serialised with `dataUpdatedAt: 0`
 * and a promise that settles over the stream.
 *
 * `hydrate()` in `query-core` replaces an existing entry only when
 * `state.dataUpdatedAt > query.state.dataUpdatedAt` (`hydration.js`). Against `0`, ANY previous visit
 * wins — in either order, which is why deferring the restore cannot help. The stale entry keeps its
 * `data`, `useSuspenseQuery` sees it and does not suspend, and the first client render paints last
 * visit's figures over the server's HTML: React #418 `args[]=text` on exactly those text nodes.
 *
 * So the rule is document-first, and it is about the FIRST RENDER only. The server's value has to win
 * that one because React compares it against the HTML it is hydrating. Anything newer arriving later —
 * the background refetch this very restore triggers — lands as an ordinary React update once the tree
 * is live, which is exactly what should happen and cannot mismatch.
 *
 * Deliberately keyed on presence, not on freshness: a document-carried query is authoritative whatever
 * its state, and comparing timestamps here would reintroduce the `dataUpdatedAt: 0` trap it exists to
 * avoid. Queries the document did NOT carry are untouched — a campaign page seen last week still gets
 * its warm start, which is the case the cache is for.
 */
export function withoutDocumentQueries<T extends PersistedQuery>(
	persisted: readonly T[],
	documentCarries: (queryHash: string) => boolean,
): T[] {
	return persisted.filter((query) => !documentCarries(query.queryHash));
}

/** A persisted entry read for its freshness rather than only its identity. */
interface TimestampedQuery extends PersistedQuery {
	state: { dataUpdatedAt: number };
}

/**
 * When this document was rendered, read off the queries it carries.
 *
 * WHY THE DOCUMENT HAS TO BE DATED (TEC-3881). `withoutDocumentQueries` hands the first render to the
 * document, which is the only way it can match the HTML. That is right, and on a document the browser
 * fetched moments ago it costs nothing, because the document IS the freshest thing available. It is not
 * free on a document the browser restored: a tab reopened the next morning re-serves yesterday's HTML
 * (the response is `no-cache`, which means revalidate, not `no-store`, so a session-restore navigation
 * reuses the disk copy), while the blob kept advancing all of the previous day behind it. Handing that
 * first render to the document then shows figures the visitor has already seen move.
 *
 * Deciding that needs the document's OWN age, and a pending query cannot give it: the whole defect is
 * that those dehydrate as `dataUpdatedAt: 0`, and a promise that settles over the stream is stamped when
 * it settles, in the browser, now — so both a fresh document and a year-old one look identical through
 * it. A query the server RESOLVED before dehydrating carries the server's real timestamp, so the newest
 * of those dates the document. The portal home always has one: its loader awaits `getMetadata` through
 * `ensureSeoData` before it returns, while deliberately not awaiting the aggregates.
 *
 * `undefined` when every carried query was pending, which is the honest answer and not a licence to
 * guess: the caller refetches nothing rather than refetching everything on a document it cannot date.
 */
export function documentRenderedAt(
	documentQueries: Iterable<{ state: { dataUpdatedAt: number } }>,
): number | undefined {
	let newest: number | undefined;
	for (const query of documentQueries) {
		const at = query.state.dataUpdatedAt;
		if (at > 0 && (newest === undefined || at > newest)) newest = at;
	}

	return newest;
}

/**
 * The dropped entries whose persisted copy was genuinely newer than the document that displaced it.
 *
 * These are the visitor's loss from the document-first rule, and the whole of it: an entry written AFTER
 * this document was rendered, discarded so the first render could match the HTML. Refetching exactly
 * these once hydration is done pays it back as an ordinary update against a live tree, which cannot
 * mismatch — the same trade `withoutDocumentQueries` describes, settled rather than left open.
 *
 * Empty on a fresh document, and that is the point of comparing against `renderedAt` rather than against
 * the displaced entry's own `dataUpdatedAt`. A pending document query sits at `0`, so every persisted
 * copy would look newer than it and a fresh load would refetch its whole cache for nothing.
 *
 * Empty too when the document could not be dated, deliberately — see {@link documentRenderedAt}.
 */
export function supersededDocumentQueries(
	persisted: readonly TimestampedQuery[],
	documentCarries: (queryHash: string) => boolean,
	renderedAt: number | undefined,
): string[] {
	if (renderedAt === undefined) return [];

	return persisted
		.filter((query) => documentCarries(query.queryHash) && query.state.dataUpdatedAt > renderedAt)
		.map((query) => query.queryHash);
}
